import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { BIVizDashboardSales } from '../sql-maps/biviz-dashboard-sales-datasource-sql-map';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';

export interface RevenueDauDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class RevenueDauDataLoaderFactory extends Factory<
    DataLoader,
    RevenueDauDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'RevenueDauDataLoaderFactory';

    getInstance(options: RevenueDauDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const revenueAgg = BIVizDashboardSales;

                // identify columns not supported in datasource
                const revenueAggDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        revenueAgg.sqlMap.columns
                    );

                const revenueAggDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        revenueAggDsColumns.missing
                    );

                const revenueAggSQL =
                    revenueAgg.sqlStatementsCompiler.compileSQLStatements(
                        revenueAggDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                const usersdashActivity = BiVizDashboardUsersActivity;

                // identify columns not supported in datasource
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );

                const usersdashActivityDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        usersdashActivityDsColumns.missing
                    );

                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );
                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';

                const runningWindow = `${
                    revenueAggSQL.splitGroupBy !== ''
                        ? 'PARTITION BY revenueAgg.split_group '
                        : ''
                }ORDER BY revenueAgg.date ASC`;

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH revenueAggBase AS
(
  SELECT 
    ds.*,
    ${revenueAggSQL.splitSelect}
    ${revenueAggSQL.datasourceSpecificSplitSelect}
    ${revenueAggSQL.cohortSplitSelect}
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      revenueAgg.sqlMap.dataset,
      revenueAgg.sqlMap.tableId
  )}\` ds
  ${revenueAggSQL.join}
  ${revenueAggSQL.where}
)

, revenueAgg AS
(
  SELECT 
    ${revenueAggSQL.splitGroupBy}${revenueAggSQL.dateTrunc()} AS date
    , ROUND(SUM(revenue_gross_usd), 2) AS revenue_gross_usd
    , ROUND(SUM(inapp_revenue_gross_usd), 2) AS inapp_revenue_gross_usd
    , ROUND(SUM(ad_revenue_gross_usd), 2) AS ad_revenue_gross_usd
    , ROUND(SUM(revenue_gross_pln), 2) AS revenue_gross_pln
    , ROUND(SUM(inapp_revenue_gross_pln), 2) AS inapp_revenue_gross_pln
    , ROUND(SUM(ad_revenue_gross_pln), 2) AS ad_revenue_gross_pln
    , ROUND(SUM(revenue_net_usd), 2) AS revenue_net_usd
    , ROUND(SUM(inapp_revenue_net_usd), 2) AS inapp_revenue_net_usd
    , ROUND(SUM(ad_revenue_net_usd), 2) AS ad_revenue_net_usd
    , ROUND(SUM(revenue_net_pln), 2) AS revenue_net_pln
    , ROUND(SUM(inapp_revenue_net_pln), 2) AS inapp_revenue_net_pln
    , ROUND(SUM(ad_revenue_net_pln), 2) AS ad_revenue_net_pln

    , SUM(daily_payers) AS sum_daily_payers
    , SUM(transactions) AS transactions
    , SUM(cnt_first_transaction) AS cnt_first_transaction
  FROM
  (
    SELECT ${revenueAggSQL.splitGroupBy}date
      , SUM(revenue_gross_usd) AS revenue_gross_usd
      , SUM(ad_revenue_gross_usd) AS ad_revenue_gross_usd
      , SUM(inapp_revenue_gross_usd) AS inapp_revenue_gross_usd
      , SUM(revenue_gross_pln)  AS revenue_gross_pln
      , SUM(inapp_revenue_gross_pln)  AS inapp_revenue_gross_pln
      , SUM(ad_revenue_gross_pln)  AS ad_revenue_gross_pln
      , SUM(revenue_net_usd)  AS revenue_net_usd
      , SUM(inapp_revenue_net_usd) AS inapp_revenue_net_usd
      , SUM(ad_revenue_net_usd) AS ad_revenue_net_usd
      , SUM(revenue_net_pln) AS revenue_net_pln
      , SUM(inapp_revenue_net_pln) AS inapp_revenue_net_pln
      , SUM(ad_revenue_net_pln) AS ad_revenue_net_pln

      , SUM(transactions) AS transactions
      , SUM(cnt_first_transaction) AS cnt_first_transaction

      , COUNT(DISTINCT payer_id) AS daily_payers
      FROM revenueAggBase
      GROUP BY ${revenueAggSQL.splitGroupBy}date 
  )
  GROUP BY ${revenueAggSQL.splitGroupBy}date 
)


, active_users AS
(
  SELECT
    ${revenueAggSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    date
    , sum_daily_active_users
    , new_users
  FROM (  
    SELECT 
      ${revenueAggSQL.cohortSplitGroupBy}${revenueAggSQL.dateTrunc()} AS date
      , SUM(daily_active_users) AS sum_daily_active_users
      , SUM(new_users) AS new_users
    FROM
    (
      SELECT 
        ${
            usersdashActivitySQL.splitGroupBy != ''
                ? `${usersdashActivitySQL.splitSelect.replace(
                      'AS split_group',
                      'AS cohort_split_group'
                  )}`
                : ''
        }date
        , COUNT(DISTINCT player_id) AS daily_active_users
        , COUNT(DISTINCT d0_player_id) AS new_users,
      FROM \`${generateTableFullPath(
          options.gcpProjectId,
          usersdashActivity.sqlMap.dataset,
          usersdashActivity.sqlMap.tableId
      )}\` ds
          ${usersdashActivitySQL.join}
          ${usersdashActivitySQL.where}  
      GROUP BY ${revenueAggSQL.cohortSplitGroupBy}date 
    )
    GROUP BY ${revenueAggSQL.cohortSplitGroupBy}date 
  )
  ${
      // if split revenue specific columns are selected, then we need to add them to the active users query
      revenueAggSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${revenueAggSQL.datasourceSpecificSplitGroupAlias} FROM revenueAggBase)`
          : ''
  }
)

SELECT
${
    revenueAggSQL.splitGroupBy !== ''
        ? `revenueAgg.split_group AS split_group, `
        : ''
}revenueAgg.date AS date
  , active_users.sum_daily_active_users
  , active_users.new_users
  , revenue_gross_usd
  , inapp_revenue_gross_usd
  , ad_revenue_gross_usd
  , revenue_gross_pln
  , inapp_revenue_gross_pln
  , ad_revenue_gross_pln
  , revenue_net_usd
  , inapp_revenue_net_usd
  , ad_revenue_net_usd
  , revenue_net_pln
  , inapp_revenue_net_pln
  , ad_revenue_net_pln
  , ROUND(SUM(revenue_gross_usd) OVER (${runningWindow}), 2) AS running_revenue_gross_usd
  , ROUND(SUM(inapp_revenue_gross_usd) OVER (${runningWindow}), 2) AS inapp_running_revenue_gross_usd
  , ROUND(SUM(ad_revenue_gross_usd) OVER (${runningWindow}), 2) AS ad_running_revenue_gross_usd
  , ROUND(SUM(revenue_gross_pln) OVER (${runningWindow}), 2) AS running_revenue_gross_pln
  , ROUND(SUM(inapp_revenue_gross_pln) OVER (${runningWindow}), 2) AS inapp_running_revenue_gross_pln
  , ROUND(SUM(ad_revenue_gross_pln) OVER (${runningWindow}), 2) AS ad_running_revenue_gross_pln
  , ROUND(SUM(revenue_net_usd) OVER (${runningWindow}), 2) AS running_revenue_net_usd
  , ROUND(SUM(inapp_revenue_net_usd) OVER (${runningWindow}), 2) AS inapp_running_revenue_net_usd
  , ROUND(SUM(ad_revenue_net_usd) OVER (${runningWindow}), 2) AS ad_running_revenue_net_usd
  , ROUND(SUM(revenue_net_pln) OVER (${runningWindow}), 2) AS running_revenue_net_pln
  , ROUND(SUM(inapp_revenue_net_pln) OVER (${runningWindow}), 2) AS inapp_running_revenue_net_pln
  , ROUND(SUM(ad_revenue_net_pln) OVER (${runningWindow}), 2) AS ad_running_revenue_net_pln
  , cnt_first_transaction
  , transactions
  , ROUND(SAFE_DIVIDE(revenue_gross_usd, sum_daily_active_users), 2) AS wavg_ARPDAU_gross_usd
  , ROUND(SAFE_DIVIDE(inapp_revenue_gross_usd, sum_daily_active_users), 2) AS inapp_wavg_ARPDAU_gross_usd
  , ROUND(SAFE_DIVIDE(ad_revenue_gross_usd, sum_daily_active_users), 2) AS ad_wavg_ARPDAU_gross_usd
  , ROUND(SAFE_DIVIDE(revenue_gross_pln, sum_daily_active_users), 2) AS wavg_ARPDAU_gross_pln
  , ROUND(SAFE_DIVIDE(inapp_revenue_gross_pln, sum_daily_active_users), 2) AS inapp_wavg_ARPDAU_gross_pln
  , ROUND(SAFE_DIVIDE(ad_revenue_gross_pln, sum_daily_active_users), 2) AS ad_wavg_ARPDAU_gross_pln
  , ROUND(SAFE_DIVIDE(revenue_net_usd, sum_daily_active_users), 2) AS wavg_ARPDAU_net_usd
  , ROUND(SAFE_DIVIDE(inapp_revenue_net_usd, sum_daily_active_users), 2) AS inapp_wavg_ARPDAU_net_usd
  , ROUND(SAFE_DIVIDE(ad_revenue_net_usd, sum_daily_active_users), 2) AS ad_wavg_ARPDAU_net_usd
  , ROUND(SAFE_DIVIDE(revenue_net_pln, sum_daily_active_users), 2) AS wavg_ARPDAU_net_pln
  , ROUND(SAFE_DIVIDE(inapp_revenue_net_pln, sum_daily_active_users), 2) AS inapp_wavg_ARPDAU_net_pln
  , ROUND(SAFE_DIVIDE(ad_revenue_net_pln, sum_daily_active_users), 2) AS ad_wavg_ARPDAU_net_pln
  , ROUND(SAFE_DIVIDE(sum_daily_payers, sum_daily_active_users), 4) AS wavg_conversion_rate
FROM revenueAgg
LEFT JOIN active_users
ON revenueAgg.date = active_users.date
${
    revenueAggSQL.splitGroupBy !== ''
        ? `AND revenueAgg.split_group = active_users.split_group`
        : ''
}
ORDER BY ${
                    revenueAggSQL.splitGroupBy !== ''
                        ? `revenueAgg.split_group, `
                        : ''
                }revenueAgg.date ASC
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: revenueAggDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        revenueAggDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
